import { useState, useEffect } from 'react'
import { useStore } from '../store'
import { Upload, Trash2, FileText } from 'lucide-react'
import api from '../services/api'

interface FileInfo {
  filename: string
  chunks_count: number
  total_size_estimate: number
}

const FILE_CATEGORIES = [
  {
    key: 'tableurs',
    label: 'Concerts / Tableurs',
    icon: '📊',
    color: '#10b981',
    extensions: ['.xlsx', '.xls', '.csv'],
  },
  {
    key: 'images',
    label: 'Images',
    icon: '🖼️',
    color: '#8b5cf6',
    extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  },
  {
    key: 'videos',
    label: 'Vidéos',
    icon: '🎬',
    color: '#f59e0b',
    extensions: ['.mp4', '.mov', '.avi', '.mkv'],
  },
  {
    key: 'documents',
    label: 'Documents',
    icon: '📄',
    color: '#3b82f6',
    extensions: ['.pdf', '.md', '.txt', '.docx', '.json', '.yaml', '.yml', '.html', '.htm'],
  },
  {
    key: 'archives',
    label: 'Archives ZIP',
    icon: '🗜️',
    color: '#f97316',
    extensions: ['.zip'],
  },
]

function getCategory(filename: string) {
  const ext = '.' + filename.split('.').pop()?.toLowerCase()
  return FILE_CATEGORIES.find(c => c.extensions.includes(ext)) ?? FILE_CATEGORIES[3]
}

export default function KnowledgePage() {
  const [files, setFiles] = useState<FileInfo[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<string>('')

  const { knowledge, fetchKnowledge } = useStore()

  useEffect(() => {
    loadFiles()
    fetchKnowledge()
  }, [fetchKnowledge])

  const loadFiles = async () => {
    try {
      const response = await api.get('/knowledge/files')
      setFiles(response.data.files || [])
    } catch (err) {
      console.error('Erreur chargement fichiers:', err)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    setUploadProgress(`Upload ${file.name}...`)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/knowledge/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      })

      setUploadProgress(`✅ ${response.data.chunks_count} chunks importés`)
      setTimeout(() => setUploadProgress(''), 3000)
      await loadFiles()
      await fetchKnowledge()
    } catch (err) {
      setUploadProgress('❌ Erreur upload')
      console.error('Upload error:', err)
    } finally {
      setIsUploading(false)
      e.target.value = ''
    }
  }

  const handleDeleteFile = async (filename: string) => {
    if (!confirm(`Supprimer ${filename} ?`)) return
    try {
      await api.delete(`/knowledge/files/${encodeURIComponent(filename)}`)
      await loadFiles()
      await fetchKnowledge()
    } catch (err) {
      console.error('Erreur suppression:', err)
    }
  }

  const filesByCategory = FILE_CATEGORIES.map(cat => ({
    ...cat,
    files: files.filter(f => {
      const ext = '.' + f.filename.split('.').pop()?.toLowerCase()
      return cat.extensions.includes(ext)
    }),
  })).filter(cat => cat.files.length > 0)

  return (
    <div style={{ backgroundColor: '#111827', height: '100%', overflowY: 'auto', padding: '32px' }}>
      <style>{`
        .file-row:hover { background-color: #27323f !important; border-color: #4b5563 !important; }
        .delete-btn:hover { color: #dc2626 !important; }
        .upload-zone:hover { border-color: #db2777 !important; background-color: #1a1f2e !important; }
      `}</style>

      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>

        {/* Header */}
        <h1 style={{ color: '#ffffff', fontSize: '28px', fontWeight: 'bold', marginBottom: '32px' }}>
          📚 Base de Connaissances
        </h1>

        {/* Zone upload */}
        <label className="upload-zone" style={{
          display: 'block',
          border: '2px dashed #9333ea',
          borderRadius: '10px',
          padding: '32px',
          marginBottom: '24px',
          textAlign: 'center',
          cursor: isUploading ? 'not-allowed' : 'pointer',
          transition: 'border-color 0.2s, background-color 0.2s',
          backgroundColor: '#1f2937',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <Upload size={36} color={isUploading ? '#6b7280' : '#a855f7'} />
            <div>
              <p style={{ color: '#f3f4f6', fontSize: '17px', fontWeight: '600', margin: '0 0 6px 0' }}>
                {isUploading ? 'Import en cours...' : 'Importer un fichier'}
              </p>
              <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>
                📊 xlsx, csv &nbsp;|&nbsp; 🖼️ jpg, png &nbsp;|&nbsp; 🎬 mp4, mov &nbsp;|&nbsp; 📄 pdf, md, txt, docx, json, yaml, html
              </p>
            </div>
          </div>
          <input
            type="file"
            accept=".md,.pdf,.txt,.docx,.csv,.json,.yaml,.yml,.html,.htm,.xlsx,.xls,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.mkv,.zip"
            onChange={handleFileUpload}
            disabled={isUploading}
            style={{ display: 'none' }}
          />
        </label>

        {/* Barre de progression */}
        {uploadProgress && (
          <div style={{
            backgroundColor: uploadProgress.startsWith('❌') ? '#450a0a' : '#0f2847',
            color: uploadProgress.startsWith('❌') ? '#fca5a5' : '#93c5fd',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '14px',
          }}>
            {uploadProgress}
          </div>
        )}

        {/* Fichiers par catégorie */}
        {files.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <FileText size={40} color="#374151" style={{ marginBottom: '12px' }} />
            <p style={{ margin: 0 }}>Aucun fichier importé</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', marginBottom: '40px' }}>
            {filesByCategory.map(cat => (
              <div key={cat.key}>
                {/* En-tête catégorie */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  borderBottom: `3px solid ${cat.color}`,
                  paddingBottom: '8px',
                  marginBottom: '16px',
                }}>
                  <span style={{ fontSize: '20px' }}>{cat.icon}</span>
                  <h2 style={{ color: cat.color, fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                    {cat.label}
                  </h2>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>({cat.files.length})</span>
                </div>

                {/* Liste fichiers */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {cat.files.map(file => (
                    <div
                      key={file.filename}
                      className="file-row"
                      style={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        padding: '14px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'background-color 0.2s, border-color 0.2s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '20px', flexShrink: 0 }}>{cat.icon}</span>
                        <div style={{ minWidth: 0 }}>
                          <p style={{
                            color: '#f3f4f6',
                            fontWeight: '500',
                            margin: '0 0 2px 0',
                            fontSize: '14px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {file.filename}
                          </p>
                          <p style={{ color: '#6b7280', fontSize: '12px', margin: 0 }}>
                            {file.chunks_count} chunks
                          </p>
                        </div>
                      </div>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteFile(file.filename)}
                        style={{
                          color: '#ef4444',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          flexShrink: 0,
                          transition: 'color 0.2s',
                        }}
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chunks indexés */}
        {knowledge.length > 0 && (
          <div>
            <h2 style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
              Chunks indexés ({knowledge.length})
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
              {knowledge.slice(0, 10).map(chunk => {
                const cat = getCategory(chunk.filename)
                return (
                  <div key={chunk.id} style={{
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '13px',
                  }}>
                    <p style={{ color: cat.color, fontSize: '11px', margin: '0 0 4px 0', fontWeight: '600' }}>
                      {cat.icon} {chunk.filename}
                    </p>
                    <p style={{
                      color: '#9ca3af',
                      margin: 0,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {chunk.content}
                    </p>
                  </div>
                )
              })}
              {knowledge.length > 10 && (
                <p style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', margin: '8px 0 0 0' }}>
                  ... et {knowledge.length - 10} autres chunks
                </p>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
